import { useEffect, useMemo, useRef, useState } from "react";
import { Building2, CalendarDays, CalendarPlus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { inr } from "@/lib/format";
import { PaginationFooter } from "@/components/PaginationFooter";

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

export function dateKey(value) {
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function addDays(value, days) {
  const d = new Date(value);
  d.setDate(d.getDate() + days);
  return d;
}

function overlapsDay(start, end, day) {
  if (!start || !end) return false;
  const dayStart = new Date(`${dateKey(day)}T00:00:00`);
  const dayEnd = new Date(`${dateKey(day)}T23:59:59`);
  return new Date(start) <= dayEnd && new Date(end) >= dayStart;
}

function availabilityForDay(row, day) {
  const schedules = asArray(row.schedules);
  const maintenance = schedules.find((s) => String(s.type || "").toLowerCase() === "maintenance" && overlapsDay(s.start_date, s.end_date, day));
  if (maintenance) {
    return { label: "Maintenance", short: "M", className: "bg-blue-100 text-blue-800 border-blue-200", title: maintenance.notes || "Maintenance" };
  }
  const booking = schedules.find((s) => String(s.type || "").toLowerCase() === "booking" && overlapsDay(s.start_date, s.end_date, day));
  if (booking) {
    return { label: "Booked", short: "B", className: "bg-emerald-100 text-emerald-800 border-emerald-200", title: booking.client_name || "Booked" };
  }
  if (overlapsDay(row.campaign_start, row.campaign_end, day)) {
    return { label: "Booked", short: "B", className: "bg-emerald-100 text-emerald-800 border-emerald-200", title: row.client_name || "Booked" };
  }
  return { label: "Available", short: "A", className: "bg-blue-50 text-blue-700 border-blue-200", title: "Available" };
}

export function isAvailableForWindow(row, dayList) {
  return dayList.every((day) => availabilityForDay(row, day).label === "Available");
}

function AvailabilityCompactCards({ rows, dayList, onOpen, selectedIds, onToggleSelected, canCreateCampaign }) {
  return (
    <div className="divide-y divide-slate-100 md:hidden">
      {rows.map((row) => {
        const fullyAvailable = isAvailableForWindow(row, dayList);
        const visibleDays = dayList.slice(0, 14);
        return (
          <div key={row.id} className="p-4">
            <div className="flex items-start gap-3">
              {canCreateCampaign && (
                <Checkbox
                  checked={selectedIds.includes(row.id)}
                  disabled={!fullyAvailable}
                  onCheckedChange={(checked) => onToggleSelected(row.id, checked)}
                  aria-label={`Select ${row.hoarding_id}`}
                  className="mt-1"
                />
              )}
              <button type="button" onClick={() => onOpen(row.id)} className="min-w-0 flex-1 text-left">
                <div className="flex flex-wrap items-center gap-2">
                  <h4 className="truncate text-sm font-semibold text-slate-950">{row.name}</h4>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${fullyAvailable ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                    {fullyAvailable ? "Fully free" : "Mixed"}
                  </span>
                </div>
                <p className="mt-1 truncate text-xs text-slate-500">
                  <span className="font-mono font-semibold text-slate-700">{row.hoarding_id}</span>
                  {[row.area, row.city].filter(Boolean).length ? ` · ${[row.area, row.city].filter(Boolean).join(", ")}` : ""}
                </p>
                <p className="mt-1 text-[11px] text-slate-400">
                  {[row.type, row.monthly_price ? inr(row.monthly_price) : ""].filter(Boolean).join(" · ")}
                </p>
              </button>
            </div>

            <div className="mt-3 grid grid-cols-7 gap-1.5">
              {visibleDays.map((day) => {
                const state = availabilityForDay(row, day);
                return (
                  <div key={`${row.id}-${dateKey(day)}`} className="text-center">
                    <div className="mb-1 text-[10px] font-medium text-slate-400">{day.toLocaleDateString("en-IN", { day: "2-digit" })}</div>
                    <div className={`mx-auto flex h-8 w-full items-center justify-center rounded-lg border text-[11px] font-bold ${state.className}`} title={`${state.label}: ${state.title}`}>
                      {state.short}
                    </div>
                  </div>
                );
              })}
            </div>
            {dayList.length > visibleDays.length && (
              <p className="mt-2 text-[11px] text-slate-400">Showing first 14 days. Use desktop/tablet width for the full {dayList.length}-day grid.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AvailabilityCalendar({
  data,
  loading,
  days,
  pageSize,
  onOpen,
  selectedIds,
  onToggleSelected,
  canCreateCampaign,
  onStartCampaign,
  controls,
  filterbar,
  lowerControls,
  hoardingHeader,
}) {
  const rows = asArray(data?.hoardings);
  const [page, setPage] = useState(1);
  const rowRefs = useRef([]);
  const paginationRef = useRef(null);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const dayList = useMemo(() => {
    const from = data?.from ? new Date(`${data.from}T00:00:00`) : new Date();
    return Array.from({ length: days }, (_, i) => addDays(from, i));
  }, [data?.from, days]);

  const summary = useMemo(() => {
    const totals = { available: 0, booked: 0, maintenance: 0, fullyAvailable: 0 };
    rows.forEach((row) => {
      if (isAvailableForWindow(row, dayList)) totals.fullyAvailable += 1;
      dayList.forEach((day) => {
        const state = availabilityForDay(row, day).label;
        if (state === "Available") totals.available += 1;
        if (state === "Booked") totals.booked += 1;
        if (state === "Maintenance") totals.maintenance += 1;
      });
    });
    return totals;
  }, [dayList, rows]);

  useEffect(() => { setPage(1); }, [data?.from, days, pageSize, rows.length]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [page, totalPages]);

  function focusRow(index) {
    rowRefs.current[index]?.focus();
  }

  function handleRowKeyDown(event, row, index) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (index < pageRows.length - 1) focusRow(index + 1);
      else paginationRef.current?.focus();
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      if (index > 0) focusRow(index - 1);
      return;
    }
    if (event.key === "Enter") {
      const fullyAvailable = isAvailableForWindow(row, dayList);
      if (canCreateCampaign && fullyAvailable) {
        event.preventDefault();
        onToggleSelected(row.id, !selectedIds.includes(row.id));
      }
    }
  }

  function handlePaginationKeyDown(event) {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      focusRow(pageRows.length - 1);
      return;
    }
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      if (page > 1) setPage(page - 1);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      if (page < totalPages) setPage(page + 1);
    }
  }

  return (
    <div className="hx-data-panel">
      <header>
        <div>
          <h2 className="flex items-center gap-2">
            <CalendarDays size={16} className="text-blue-600" />
            Availability Calendar
          </h2>
        </div>
        <div className="hx-availability-header-actions">
          {controls}
          {loading && rows.length > 0 && (
            <span className="hx-register-refresh" role="status">
              <span aria-hidden="true" />
              Updating
            </span>
          )}
          <div className="flex flex-wrap gap-2 text-xs font-semibold">
            <span className="hx-status-pill tone-rose">Fully free {summary.fullyAvailable}</span>
            <span className="hx-status-pill tone-rose">Available {summary.available}</span>
            <span className="hx-status-pill tone-emerald">Booked {summary.booked}</span>
            <span className="hx-status-pill tone-blue">Maintenance {summary.maintenance}</span>
          </div>
        </div>
      </header>

      {filterbar}
      {lowerControls}

      {selectedIds.length > 0 && canCreateCampaign && (
        <div className="flex flex-wrap items-center gap-3 border-b border-blue-100 bg-blue-50 px-5 py-3">
          <span className="text-sm font-semibold text-red-950">{selectedIds.length} fully available site{selectedIds.length === 1 ? "" : "s"} selected</span>
          <button type="button" onClick={onStartCampaign} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700">
            <CalendarPlus size={14} /> Start campaign
          </button>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <HoardexaLoader label="Loading availability" />
      ) : rows.length === 0 ? (
        <div className="p-12 text-center">
          <Building2 size={32} className="mx-auto text-slate-300" strokeWidth={1.2} />
          <p className="mt-2 text-sm font-semibold text-slate-700">No hoardings match this availability view.</p>
        </div>
      ) : (
        <>
          <div className={loading ? "is-refreshing" : ""}>
            <AvailabilityCompactCards
              rows={pageRows}
              dayList={dayList}
              onOpen={onOpen}
              selectedIds={selectedIds}
              onToggleSelected={onToggleSelected}
              canCreateCampaign={canCreateCampaign}
            />
          </div>
          <div className={`hidden overflow-hidden md:block ${loading ? "is-refreshing" : ""}`}>
            <table className="w-full table-fixed text-xs">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--hx-border)", background: "color-mix(in srgb, var(--hx-bg) 60%, var(--hx-surface))" }}>
                  <th className="w-[168px] bg-inherit px-2.5 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-slate-500 lg:w-[200px] xl:w-[230px]">{hoardingHeader || "Hoarding"}</th>
                  {dayList.map((day) => (
                    <th key={dateKey(day)} className="px-0.5 py-2 text-center text-[9px] font-bold text-slate-500">
                      <div>{day.toLocaleDateString("en-IN", { day: "2-digit" })}</div>
                      <div className="hidden font-medium text-slate-400 xl:block">{day.toLocaleDateString("en-IN", { weekday: "short" })}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row, index) => (
                  <tr
                    key={row.id}
                    ref={(node) => { rowRefs.current[index] = node; }}
                    tabIndex={0}
                    onKeyDown={(event) => handleRowKeyDown(event, row, index)}
                    className="border-b hover:bg-blue-50/30 focus:bg-blue-50/60 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/30"
                    style={{ borderColor: "color-mix(in srgb, var(--hx-border) 65%, transparent)" }}
                  >
                    <td className="bg-white px-2.5 py-2">
                      <div className="flex items-start gap-2">
                        {canCreateCampaign && (
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            disabled={!isAvailableForWindow(row, dayList)}
                            onCheckedChange={(checked) => onToggleSelected(row.id, checked)}
                            aria-label={`Select ${row.hoarding_id}`}
                            className="mt-0.5 h-4 w-4"
                          />
                        )}
                        <button type="button" className="min-w-0 text-left" onClick={() => onOpen(row.id)}>
                          <div className="truncate text-xs font-bold text-slate-900">{row.name}</div>
                          <div className="truncate text-[10px] text-slate-500">
                            <span className="font-mono">{row.hoarding_id}</span>
                            {row.city ? ` · ${row.city}` : ""}
                          </div>
                          <div className="mt-0.5 hidden truncate text-[10px] text-slate-400 lg:block">
                            {row.type}{row.monthly_price ? ` · ${inr(row.monthly_price)}` : ""}
                          </div>
                        </button>
                      </div>
                    </td>
                    {dayList.map((day) => {
                      const state = availabilityForDay(row, day);
                      return (
                        <td key={`${row.id}-${dateKey(day)}`} className="px-0.5 py-1.5 text-center">
                          <div
                            className={`mx-auto flex h-6 w-full max-w-7 items-center justify-center rounded-md border text-[9px] font-black ${state.className}`}
                            title={`${state.label}: ${state.title}`}
                          >
                            {state.short}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!loading && rows.length > 0 && (
        <div
          ref={paginationRef}
          tabIndex={0}
          onKeyDown={handlePaginationKeyDown}
          className="focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/30"
        >
          <PaginationFooter page={page} pageSize={pageSize} total={rows.length} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
